import React, { useState, useMemo, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ApplyNowModal = ({ isOpen, onClose, preSelectedLocation = '' }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');

  // Sample locations - replace with your actual data source
  const locations = [
    { id: 1, name: 'Johannesburg', image: 'https://images.unsplash.com/photo-1577948000111-9c970dfe3743?w=400' },
    { id: 2, name: 'Benoni', image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=400' },
    { id: 3, name: 'Pretoria', image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=400' },
    { id: 4, name: 'Witbank', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400' },
  ];

  // Sample properties per location - replace with your actual data
  const propertiesMap = {
    'Johannesburg': [
      { id: 1, name: 'Sandton Heights', type: 'Residential', image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400', status: 'Rent', price: 'R15,000/month', beds: 2, baths: 2, sqft: 1200 },
      { id: 2, name: 'Rosebank Plaza', type: 'Commercial', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400', status: 'Sale', price: 'R2,500,000', beds: 0, baths: 0, sqft: 850 },
      { id: 3, name: 'Midrand Complex', type: 'Residential', image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400', status: 'Rent', price: 'R18,000/month', beds: 3, baths: 2, sqft: 1500 },
      { id: 4, name: 'Fourways Estate', type: 'Residential', image: 'https://images.unsplash.com/photo-1558036117-15d82a90b9b1?w=400', status: 'Sale', price: 'R3,200,000', beds: 4, baths: 3, sqft: 2200 },
    ],
    'Benoni': [
      { id: 5, name: 'Benoni Central', type: 'Commercial', image: 'https://images.unsplash.com/photo-1565402170291-8491f14678db?w=400', status: 'Rent', price: 'R8,500/month', beds: 0, baths: 0, sqft: 600 },
      { id: 6, name: 'Lakeside View', type: 'Residential', image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400', status: 'Rent', price: 'R12,000/month', beds: 3, baths: 2, sqft: 1400 },
    ],
    'Pretoria': [
      { id: 7, name: 'Hatfield Square', type: 'Residential', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400', status: 'Rent', price: 'R14,500/month', beds: 2, baths: 2, sqft: 1100 },
      { id: 8, name: 'Menlyn Park', type: 'Commercial', image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400', status: 'Sale', price: 'R1,800,000', beds: 0, baths: 0, sqft: 750 },
    ],
    'Witbank': [
      { id: 9, name: 'Witbank Plaza', type: 'Commercial', image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400', status: 'Rent', price: 'R7,000/month', beds: 0, baths: 0, sqft: 500 },
      { id: 10, name: 'Emalahleni Estate', type: 'Residential', image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=400', status: 'Sale', price: 'R1,500,000', beds: 3, baths: 2, sqft: 1300 },
    ],
  };

  const currentProperties = selectedLocation ? (propertiesMap[selectedLocation] || []) : [];

  // Generate room options based on selected property
  const rooms = useMemo(() => {
    const selectedProp = currentProperties.find(p => p.name === selectedProperty);
    if (!selectedProp) return [];

    const basePrice = parseInt(selectedProp.price.replace(/\D/g, ''));
    const isRent = selectedProp.price.includes('month');

    if (selectedProp.type === 'Residential' && selectedProp.beds > 0) {
      return [
        {
          id: 1,
          name: `Standard ${selectedProp.beds} Bedroom Unit`,
          price: selectedProp.price,
          description: `${selectedProp.beds} bed, ${selectedProp.baths} bath, ${selectedProp.sqft} sqft - Standard finishes`,
          image: selectedProp.image
        },
        {
          id: 2,
          name: `Deluxe ${selectedProp.beds} Bedroom Unit`,
          price: isRent ? `R${Math.round(basePrice * 1.15).toLocaleString()}/month` : `R${Math.round(basePrice * 1.15).toLocaleString()}`,
          description: `${selectedProp.beds} bed, ${selectedProp.baths} bath, ${selectedProp.sqft} sqft - Upgraded finishes with balcony`,
          image: selectedProp.image
        },
        {
          id: 3,
          name: `Premium ${selectedProp.beds} Bedroom Unit`,
          price: isRent ? `R${Math.round(basePrice * 1.3).toLocaleString()}/month` : `R${Math.round(basePrice * 1.3).toLocaleString()}`,
          description: `${selectedProp.beds} bed, ${selectedProp.baths} bath, ${selectedProp.sqft} sqft - Premium finishes with smart home`,
          image: selectedProp.image
        },
        {
          id: 4,
          name: `Penthouse ${selectedProp.beds} Bedroom Suite`,
          price: isRent ? `R${Math.round(basePrice * 1.5).toLocaleString()}/month` : `R${Math.round(basePrice * 1.5).toLocaleString()}`,
          description: `${selectedProp.beds} bed, ${selectedProp.baths + 1} bath, ${selectedProp.sqft + 500} sqft - Luxury with terrace`,
          image: selectedProp.image
        }
      ];
    } else if (selectedProp.type === 'Commercial') {
      return [
        {
          id: 1,
          name: 'Standard Office Space',
          price: selectedProp.price,
          description: `${selectedProp.sqft} sqft - Open plan with basic amenities`,
          image: selectedProp.image
        },
        {
          id: 2,
          name: 'Executive Office Suite',
          price: isRent ? `R${Math.round(basePrice * 1.25).toLocaleString()}/month` : `R${Math.round(basePrice * 1.25).toLocaleString()}`,
          description: `${selectedProp.sqft} sqft - Private offices & meeting rooms`,
          image: selectedProp.image
        },
        {
          id: 3,
          name: 'Premium Corporate Suite',
          price: isRent ? `R${Math.round(basePrice * 1.5).toLocaleString()}/month` : `R${Math.round(basePrice * 1.5).toLocaleString()}`,
          description: `${selectedProp.sqft + 300} sqft - Full floor with boardroom`,
          image: selectedProp.image
        }
      ];
    } else {
      return [
        {
          id: 1,
          name: 'Standard Unit',
          price: selectedProp.price,
          description: `${selectedProp.sqft} sqft - Standard features`,
          image: selectedProp.image
        },
        {
          id: 2,
          name: 'Premium Unit',
          price: isRent ? `R${Math.round(basePrice * 1.3).toLocaleString()}/month` : `R${Math.round(basePrice * 1.3).toLocaleString()}`,
          description: `${selectedProp.sqft + 200} sqft - Upgraded features`,
          image: selectedProp.image
        }
      ];
    }
  }, [selectedProperty, currentProperties]);

  // Handle pre-selected location
  useEffect(() => {
    if (preSelectedLocation && isOpen) {
      setSelectedLocation(preSelectedLocation);
      setCurrentStep(2);
    } else if (isOpen && !preSelectedLocation) {
      setCurrentStep(1);
      setSelectedLocation('');
    }
  }, [preSelectedLocation, isOpen]);

  const handleNext = () => {
    if (currentStep === 1 && selectedLocation) {
      setCurrentStep(2);
    } else if (currentStep === 2 && selectedProperty) {
      setCurrentStep(3);
    } else if (currentStep === 3 && selectedRoom) {
      const selectedPropData = currentProperties.find(p => p.name === selectedProperty);
      const selectedRoomData = rooms.find(r => r.name === selectedRoom);
      
      navigate('/apply', {
        state: {
          prefilledData: {
            location: selectedLocation,
            propertyName: selectedProperty,
            propertyCategory: selectedPropData?.type || '',
            roomType: selectedRoom,
            price: selectedRoomData?.price?.replace(/\D/g, '') || '',
            size: selectedPropData?.sqft || '',
            bedrooms: selectedPropData?.beds || '',
            bathrooms: selectedPropData?.baths || ''
          }
        }
      });
      onClose();
      resetModal();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1 && !(currentStep === 2 && preSelectedLocation)) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 2) {
        setSelectedProperty('');
        setSelectedRoom('');
      } else if (currentStep === 3) {
        setSelectedRoom('');
      }
    }
  };

  const resetModal = () => {
    setCurrentStep(preSelectedLocation ? 2 : 1);
    setSelectedLocation(preSelectedLocation || '');
    setSelectedProperty('');
    setSelectedRoom('');
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetModal, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
        onClick={handleClose}
      />

      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="relative bg-gradient-to-r from-orange-400 to-orange-500 p-6 sm:p-8 text-center">
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
              APPLY <span className="font-normal">NOW</span>
            </h2>
            <p className="text-white/90 text-sm">Step {currentStep} of 3</p>
          </div>

          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-[calc(90vh-220px)]">
            {preSelectedLocation && selectedLocation && currentStep > 1 && (
              <div className="mb-6 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  <span className="font-semibold">Selected Location:</span> {selectedLocation}
                </p>
              </div>
            )}

            {currentStep === 1 && (
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8">
                  CHOOSE LOCATION
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {locations.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => setSelectedLocation(location.name)}
                      className={`group relative overflow-hidden rounded-xl transition-all ${
                        selectedLocation === location.name
                          ? 'ring-4 ring-orange-500 scale-105'
                          : 'hover:scale-105'
                      }`}
                    >
                      <div className="aspect-square">
                        <img
                          src={location.image}
                          alt={location.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-500 rounded-full flex items-center justify-center mb-2">
                            <span className="text-white font-bold text-xs sm:text-sm">RX</span>
                          </div>
                          <p className="text-white font-semibold text-xs sm:text-sm">Raulfex</p>
                          <p className="text-white font-bold text-sm sm:text-lg">{location.name}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8">
                  CHOOSE PROPERTY
                </h3>
                {currentProperties.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {currentProperties.map((property) => (
                      <button
                        key={property.id}
                        onClick={() => setSelectedProperty(property.name)}
                        className={`group relative overflow-hidden rounded-xl transition-all ${
                          selectedProperty === property.name
                            ? 'ring-4 ring-orange-500 scale-105'
                            : 'hover:scale-105'
                        }`}
                      >
                        <div className="aspect-video">
                          <img
                            src={property.image}
                            alt={property.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                          <div className="absolute top-3 left-3 flex gap-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              property.type === 'Residential' ? 'bg-green-500' :
                              property.type === 'Commercial' ? 'bg-blue-500' :
                              'bg-gray-700'
                            } text-white`}>
                              {property.type}
                            </span>
                            <span className="bg-white text-gray-900 text-xs font-semibold px-2 py-1 rounded-full">
                              {property.status}
                            </span>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                            <p className="text-white font-bold text-base sm:text-lg mb-1">
                              {property.name}
                            </p>
                            <p className="text-white/90 text-xs sm:text-sm">{property.price}</p>
                            {property.beds > 0 && (
                              <p className="text-white/80 text-xs mt-1">
                                {property.beds} bed • {property.baths} bath • {property.sqft} sqft
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No properties available in {selectedLocation}</p>
                  </div>
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">CHOOSE UNIT</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room.name)}
                      className={`text-left rounded-xl overflow-hidden transition-all ${
                        selectedRoom === room.name
                          ? 'ring-4 ring-orange-500 scale-105'
                          : 'border-2 border-gray-200 hover:border-orange-500'
                      }`}
                    >
                      <div className="relative aspect-video">
                        <img
                          src={room.image}
                          alt={room.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 left-3 bg-black/70 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                          {room.price}
                        </div>
                      </div>
                      <div className="p-3 sm:p-4">
                        <h4 className="font-bold text-base sm:text-lg mb-1">{room.name}</h4>
                        <p className="text-gray-600 text-xs sm:text-sm">{room.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-4 sm:p-6 bg-gray-50 flex justify-between items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={preSelectedLocation && currentStep === 2}
                className="px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-900 text-gray-900 font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !selectedLocation) ||
                (currentStep === 2 && !selectedProperty) ||
                (currentStep === 3 && !selectedRoom)
              }
              className="px-6 sm:px-8 py-2 sm:py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {currentStep === 3 ? 'Continue to Application' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplyNowModal;